import React from "react"
import { compose } from "ramda"
import { withAuthProvider } from "../../lib/auth"
import { CookiesProvider } from "react-cookie"
import styled from "styled-components"
import MediaQuery from "react-responsive"
import MembershipFinance from "../widgets/MembershipFinance"
import ProgramEnrollmentCard from "../widgets/ProgramEnrollmentCard"
import { ImgFrame, Headline } from "../widgets/shared"
import Briefing from "../widgets/Briefing"
import AnnouncementsCard from "../widgets/Announcements"
import MembershipJourneyLoading from "../widgets/MembershipJourney/Loading"
import Resources from "../widgets/Resources"
import FlexRow from "../FlexRow"
import News from "../widgets/News"
import { primary, white } from "../../assets/colors"
import {
  isMember,
  isStudent,
  isAssociateTrack,
  isAffiliate,
  isAffiliateTrack,
  hasMembershipType,
  shouldNotSeeGeneric,
  isQualifierEligible,
  shouldSeeJobRecommendations,
  isTerminatedStaff,
  shouldSeeProspectDashboard,
  shouldSeeMembershipJourneyDashboard,
} from "../utils"
import { setLoading, removeLoading } from "../queries/loading"
import { useEffectiveMe } from "../Terminated/TerminationStatusContext"
import golfImg from "../../assets/img/golfduesimg.png"
import EducationAppDecisionModal from "../widgets/EducationAppDecisionModal"
import JobRecommendation from "../widgets/JobRecommendation"
import JobSeekerDashboard from "./JobSeekerDashboard"
import MarketingBanner from "../Banners"
import MemberResources from "../widgets/Resources/MemberResources"
import ProspectDashboard from "../ProspectDashboard"
import MemberApplyEntry from "../widgets/MemberApplyEntry"
import { withProspectDateQuery } from "../../utils/prospect/withProspectDateQuery"
import { withOrdersQuery } from "./withOrdersQuery"
import { AnnualSurvey } from "../widgets/AnnualSurvey"
import { MembershipJourneyDashboardWrapper } from "../widgets/MembershipJourney"
import { useMembershipJourney } from "../../hooks/useMembershipJourney"
import { useMemberApplicationEligibility } from "../../hooks/useMemberApplicationEligibility"
import { useMemberApplyPath } from "../../hooks/useMemberApplyPath"
import { useReciprocityApplication } from "../../hooks/useReciprocityApplication"
import ReciprocityApplicationEntry from "../widgets/ReciprocityApplication"
import { replacesPathToMembership } from "../../utils/reciprocityApplication"
import { shouldRoutePendingAffiliate } from "../utils"

const FinanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 375px;
  @media (min-width: 768px) {
    flex-basis: 48%;
  }
  @media (min-width: 992px) {
    flex-basis: 33%;
    height: 100%;
  }

  @media (max-width: 767.98px) {
    flex-basis: 100%;
  }
`

export const MembershipDashboardContainer = styled(FlexRow)`
  align-items: flex-start;
  row-gap: 20px;
`

export const Dashboard = ({
  me,
  authReady,
  currentMember,
  currentPerson,
  history,
}) => {
  const effectiveMe = useEffectiveMe(me)
  const { person, loading: personLoading } = useMembershipJourney()
  useMemberApplicationEligibility({ skip: !authReady })
  // Auth USER_QUERY (developers) is legacy type/class; Coach Tools journey person
  // carries registeredAffiliate + mainProgramTypeCode. Prefer journey for gates.
  const routingMe = person || me
  // When auth me looks generic, wait for journey person before Job Seeker so
  // Coach Tools AF Affiliates are not flashed onto the wrong shell.
  const awaitingJourneyPerson =
    personLoading && !person && !shouldNotSeeGeneric(me)
  const {
    canApplyViaReciprocity: canApplyOnceLoaded,
    eligibilityPath,
    loading: applyPathLoading,
  } = useMemberApplyPath({
    skip: !authReady || isMember(routingMe),
  })
  const { reciprocityApplication, loading: applicationLoading } =
    useReciprocityApplication({
      eligibilityPath,
      skip: !authReady || isMember(routingMe),
    })
  // Hold the membership entry until both requests settle so the apply card,
  // chooser, or tracker does not show first and then swap out.
  const membershipEntryLoading = applyPathLoading || applicationLoading
  const canApplyViaReciprocity = canApplyOnceLoaded && !membershipEntryLoading
  const replacesPath = replacesPathToMembership(reciprocityApplication)

  !authReady ? setLoading("dashboard") : removeLoading("dashboard")

  if (isTerminatedStaff(effectiveMe)) {
    return <JobSeekerDashboard me={effectiveMe} />
  }

  // Members never take the reciprocity path; the apply action shows only when
  // Coach Tools reports eligibilityPath RECIPROCITY. Once the candidate has an
  // application, its status replaces the apply action.
  const reciprocityEntry = reciprocityApplication ? (
    <ReciprocityApplicationEntry application={reciprocityApplication} />
  ) : null
  const memberApplyEntry = reciprocityEntry || (
    <MemberApplyEntry show={canApplyViaReciprocity} />
  )

  if (authReady && awaitingJourneyPerson) {
    return <MembershipJourneyLoading />
  }

  if (shouldSeeProspectDashboard(routingMe, currentMember)) {
    const pendingAffiliate = shouldRoutePendingAffiliate(routingMe)
    const prospectMe = person ? { ...me, ...routingMe } : me

    return (
      <>
        {replacesPath ? null : reciprocityEntry}
        <ProspectDashboard
          me={prospectMe}
          canApplyViaReciprocity={canApplyViaReciprocity}
          reciprocityApplication={replacesPath ? reciprocityApplication : null}
          membershipEntryLoading={membershipEntryLoading}
        />
        {pendingAffiliate ? (
          <MembershipDashboardContainer>
            <FinanceWrapper>
              <MembershipFinance coachToolsOrders={currentPerson?.orders} />
            </FinanceWrapper>
          </MembershipDashboardContainer>
        ) : null}
      </>
    )
  }

  return authReady && !shouldNotSeeGeneric(routingMe) ? (
    <>
      {memberApplyEntry}
      <JobSeekerDashboard
        me={me}
        hidePathToMembership={replacesPath || membershipEntryLoading}
      />
    </>
  ) : (
    <>
      {memberApplyEntry}
      <MembershipDashboardContainer>
        {isMember(routingMe) && <AnnualSurvey me={me} />}
        {personLoading && !person ? (
          <MembershipJourneyLoading />
        ) : shouldSeeMembershipJourneyDashboard(person) ? (
          <MembershipJourneyDashboardWrapper
            authReady={authReady}
            history={history}
          />
        ) : (
          <>
            <Briefing />
            <MediaQuery maxWidth={1199}>
              {isMember(routingMe) ||
              isAssociateTrack(routingMe) ||
              isAffiliate(routingMe) ||
              !hasMembershipType(routingMe) ? (
                <AnnouncementsCard card />
              ) : null}
            </MediaQuery>
          </>
        )}
        {isMember(routingMe) ||
        isAssociateTrack(routingMe) ||
        isAffiliateTrack(routingMe) ? (
          <FinanceWrapper>
            {isMember(routingMe) ? (
              <ProgramEnrollmentCard />
            ) : isAssociateTrack(routingMe) ? (
              <ImgFrame src={golfImg} color={white} />
            ) : null}
            {isMember(routingMe) ||
            isAssociateTrack(routingMe) ||
            isAffiliateTrack(routingMe) ? (
              <MembershipFinance coachToolsOrders={currentPerson?.orders} />
            ) : null}
          </FinanceWrapper>
        ) : null}
        {hasMembershipType(routingMe) ? (
          isMember(routingMe) ? (
            <MemberResources />
          ) : (
            <Resources
              flexBasis={
                isQualifierEligible(routingMe) || isStudent(routingMe)
                  ? 100
                  : null
              }
            />
          )
        ) : null}
      </MembershipDashboardContainer>
      {shouldSeeJobRecommendations(routingMe) ? (
        <JobRecommendation {...{ me }} />
      ) : null}
      <FlexRow>
        <Headline color={primary}>Top Headlines</Headline>
      </FlexRow>
      <FlexRow>
        <News me={me} />
      </FlexRow>
      <CookiesProvider>
        <EducationAppDecisionModal />
      </CookiesProvider>
    </>
  )
}

export const DashboardWithBanner = props => (
  <>
    <MarketingBanner {...props} />
    <Dashboard {...props} />
  </>
)

const DashboardWithFeatureFlags = WrappedComponent => {
  return compose(
    withProspectDateQuery,
    withOrdersQuery,
    withAuthProvider,
  )(WrappedComponent)
}

export default DashboardWithFeatureFlags(DashboardWithBanner)
